import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useApi } from '../../hooks/useApi';
import { useToast } from '../../contexts/ToastContext';
import { useTheme } from '../../contexts/ThemeContext';
import { formatDistanceToNow } from 'date-fns';
import LoadingIndicator from '../../components/LoadingIndicator';
import AdminRoute from '../../components/AdminRoute';

export default function AdminCallRecordingsPage() {
  const { execute, loading } = useApi();
  const { showSuccess, showError, showInfo } = useToast();
  const { theme } = useTheme();

  // State management
  const [recordings, setRecordings] = useState([]);
  const [selectedRecordings, setSelectedRecordings] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchFilters, setSearchFilters] = useState({
    q: '',
    file_type: 'all',
    user_id: '',
    user_email: '',
    parrot_id: '',
    has_files: 'any',
    min_duration: '',
    max_duration: '',
    deleted: 'any',
    date_from: '',
    date_to: '',
    sort_by: 'created_at',
    sort_order: 'desc'
  });
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [loadingStats, setLoadingStats] = useState(true);
  const [storageStats, setStorageStats] = useState(null);
  const [showFilePreview, setShowFilePreview] = useState(null);
  const [filePreviewData, setFilePreviewData] = useState(null);

  // Fetch recordings data with search
  const fetchRecordings = async (page = 1, customFilters = {}) => {
    try {
      const response = await execute(async (apiClient) => {
        const params = {
          page,
          limit: 50,
          ...searchFilters,
          ...customFilters
        };

        // Remove empty values
        Object.keys(params).forEach(key => {
          if (params[key] === '' || params[key] === null) {
            delete params[key];
          }
        });

        return await apiClient.get('/api/admin/call-recordings/search', { params });
      });

      if (response.success) {
        setRecordings(response.data || []);
        setPagination(response.pagination);
        setCurrentPage(page);
      }
    } catch (error) {
      showError('Failed to fetch call recordings');
      console.error('Failed to fetch recordings:', error);
    }
  };

  // Fetch storage statistics
  const fetchStorageStats = async () => {
    try {
      setLoadingStats(true);
      const response = await execute(async (apiClient) => {
        return await apiClient.get('/api/admin/call-recordings/storage-stats');
      });

      if (response.success) {
        setStorageStats(response.statistics);
      }
    } catch (error) {
      console.error('Failed to fetch storage statistics:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  // Handle file preview
  const handleFilePreview = async (recordingId, fileType) => {
    try {
      const response = await execute(async (apiClient) => {
        return await apiClient.get(`/api/admin/call-recordings/${recordingId}/file-preview`, {
          params: { type: fileType }
        });
      });

      if (response.success) {
        setFilePreviewData(response.data);
        setShowFilePreview(true);
      }
    } catch (error) {
      showError('Failed to load file preview');
    }
  };

  // Handle file view/download
  const handleFileView = async (recordingId, fileType, download = false) => {
    try {
      const response = await execute(async (apiClient) => {
        return await apiClient.get(`/api/admin/call-recordings/${recordingId}/view-file`, {
          params: { type: fileType, download: download.toString() }
        });
      });

      if (response && response.url) {
        // Create a temporary link for download
        const link = document.createElement('a');
        link.href = response.url;
        link.download = download ? `${recordingId}-${fileType}.${fileType === 'audio' ? 'wav' : fileType === 'transcript' ? 'txt' : 'raw'}` : '';
        link.target = download ? '_self' : '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        if (!download) {
          showInfo(`Opening ${fileType} file in new tab`);
        }
      }
    } catch (error) {
      showError(`Failed to ${download ? 'download' : 'view'} file`);
    }
  };

  // Handle permanent delete
  const handlePermanentDelete = async (recording) => {
    if (!confirm(`Are you sure you want to permanently delete this recording? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await execute(async (apiClient) => {
        return await apiClient.delete(`/api/admin/call-recordings/${recording.conversation_id}/permanent-delete`);
      });

      if (response.success) {
        showSuccess('Recording permanently deleted successfully');
        await fetchRecordings(currentPage);
        await fetchStorageStats();
      }
    } catch (error) {
      showError('Failed to permanently delete recording');
    }
  };

  // Handle restore
  const handleRestore = async (recording) => {
    try {
      const response = await execute(async (apiClient) => {
        return await apiClient.post(`/api/admin/call-recordings/${recording.conversation_id}/restore`);
      });

      if (response.success) {
        showSuccess('Recording restored successfully');
        await fetchRecordings(currentPage);
        await fetchStorageStats();
      }
    } catch (error) {
      showError('Failed to restore recording');
    }
  };

  // Handle bulk operations
  const handleBulkPermanentDelete = async () => {
    if (selectedRecordings.length === 0) {
      showError('No recordings selected');
      return;
    }

    if (!confirm(`Are you sure you want to permanently delete ${selectedRecordings.length} recordings? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await execute(async (apiClient) => {
        return await apiClient.post('/api/admin/call-recordings/bulk-permanent-delete', {
          conversationIds: selectedRecordings.map(r => r.conversation_id)
        });
      });

      if (response.success) {
        showSuccess(`${response.successCount} recordings permanently deleted`);
        setSelectedRecordings([]);
        setShowBulkActions(false);
        await fetchRecordings(currentPage);
        await fetchStorageStats();
      }
    } catch (error) {
      showError('Failed to delete recordings');
    }
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    const newFilters = { ...searchFilters, [key]: value };
    setSearchFilters(newFilters);
    fetchRecordings(1, newFilters);
  };

  // Toggle selection
  const toggleSelection = (recording) => {
    const isSelected = selectedRecordings.some(r => r.conversation_id === recording.conversation_id);
    if (isSelected) {
      setSelectedRecordings(prev => prev.filter(r => r.conversation_id !== recording.conversation_id));
    } else {
      setSelectedRecordings(prev => [...prev, recording]);
    }
  };

  // Select all on current page
  const selectAllOnPage = () => {
    if (selectedRecordings.length === recordings.length) {
      setSelectedRecordings([]);
    } else {
      setSelectedRecordings([...recordings]);
    }
  };

  // Format duration
  const formatDuration = (durationMs) => {
    if (!durationMs) return 'N/A';
    const seconds = Math.round(durationMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Initial data fetch
  useEffect(() => {
    fetchRecordings();
    fetchStorageStats();
  }, []);

  return (
    <AdminRoute>
      <div>
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Call Recordings Management</h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage all user call recordings, view files, and perform bulk operations
          </p>
        </div>

        {/* Storage Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {loadingStats ? (
            <div className="col-span-4">
              <LoadingIndicator type="spinner" size="small" />
            </div>
          ) : storageStats ? (
            <>
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                <div className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Recordings</div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  {storageStats.total_recordings || 0}
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                <div className="text-sm font-medium text-gray-600 dark:text-gray-400">Deleted Files</div>
                <div className="text-2xl font-bold text-red-600 dark:text-red-400 mt-1">
                  {storageStats.deleted_files || 0}
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                <div className="text-sm font-medium text-gray-600 dark:text-gray-400">Storage Used</div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  {storageStats.storage_estimate?.total_size_mb || 0} MB
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                <div className="text-sm font-medium text-gray-600 dark:text-gray-400">Avg Duration</div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  {formatDuration(storageStats.avg_stt_time_ms)}
                </div>
              </div>
            </>
          ) : (
            <div className="col-span-4 text-center text-gray-600 dark:text-gray-400">
              No statistics available
            </div>
          )}
        </div>

        {/* Search and Filters */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6 border border-gray-200 dark:border-gray-700">
          <div className="space-y-4">
            {/* Search Bar */}
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Search recordings (users, parrots, transcripts)..."
                  value={searchFilters.q}
                  onChange={(e) => handleFilterChange('q', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setSearchFilters({
                      q: '',
                      file_type: 'all',
                      user_id: '',
                      user_email: '',
                      parrot_id: '',
                      has_files: 'any',
                      min_duration: '',
                      max_duration: '',
                      deleted: 'any',
                      date_from: '',
                      date_to: '',
                      sort_by: 'created_at',
                      sort_order: 'desc'
                    });
                    fetchRecordings(1, {});
                  }}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                >
                  Clear Filters
                </button>
              </div>
            </div>

            {/* Advanced Filters */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* File Type Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">File Type</label>
                <select
                  value={searchFilters.file_type}
                  onChange={(e) => handleFilterChange('file_type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  <option value="all">All Files</option>
                  <option value="audio">Audio Only</option>
                  <option value="transcript">Transcripts Only</option>
                  <option value="raw_log">Raw Logs Only</option>
                </select>
              </div>

              {/* User Email Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">User Email</label>
                <input
                  type="email"
                  placeholder="Filter by user email"
                  value={searchFilters.user_email}
                  onChange={(e) => handleFilterChange('user_email', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* Has Files Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">File Availability</label>
                <select
                  value={searchFilters.has_files}
                  onChange={(e) => handleFilterChange('has_files', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  <option value="any">Any</option>
                  <option value="true">Has Files</option>
                  <option value="false">No Files</option>
                </select>
              </div>

              {/* Deletion Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Deletion Status</label>
                <select
                  value={searchFilters.deleted}
                  onChange={(e) => handleFilterChange('deleted', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  <option value="any">All</option>
                  <option value="false">Active</option>
                  <option value="true">Deleted</option>
                </select>
              </div>

              {/* Date Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date From</label>
                <input
                  type="date"
                  value={searchFilters.date_from}
                  onChange={(e) => handleFilterChange('date_from', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date To</label>
                <input
                  type="date"
                  value={searchFilters.date_to}
                  onChange={(e) => handleFilterChange('date_to', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* Sort Options */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sort By</label>
                <select
                  value={`${searchFilters.sort_by}-${searchFilters.sort_order}`}
                  onChange={(e) => {
                    const [sort, order] = e.target.value.split('-');
                    handleFilterChange('sort_by', sort);
                    handleFilterChange('sort_order', order);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  <option value="created_at-desc">Newest First</option>
                  <option value="created_at-asc">Oldest First</option>
                  <option value="duration-desc">Longest First</option>
                  <option value="duration-asc">Shortest First</option>
                  <option value="user_email-desc">User Email Z-A</option>
                  <option value="user_email-asc">User Email A-Z</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedRecordings.length > 0 && (
          <div className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-700 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-teal-800 dark:text-teal-200">
                {selectedRecordings.length} recording{selectedRecordings.length !== 1 ? 's' : ''} selected
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleBulkPermanentDelete}
                  className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                >
                  Delete Selected
                </button>
                <button
                  onClick={() => setSelectedRecordings([])}
                  className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700"
                >
                  Clear Selection
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Recordings Table */}
        <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-lg border border-gray-200 dark:border-gray-700">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <LoadingIndicator type="spinner" size="large" />
            </div>
          ) : recordings.length === 0 ? (
            <div className="text-center py-12 text-gray-600 dark:text-gray-400">
              <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
              </svg>
              <h3 className="text-lg font-medium mb-2">No recordings found</h3>
              <p className="text-sm">Try adjusting your search filters or check back later.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={selectedRecordings.length === recordings.length}
                        onChange={selectAllOnPage}
                        className="rounded text-teal-600 focus:ring-teal-500"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      User / Parrot
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Files
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Duration / Turns
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {recordings.map((recording) => (
                    <tr key={recording.id || recording.conversation_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-4 py-4">
                        <input
                          type="checkbox"
                          checked={selectedRecordings.some(r => r.conversation_id === recording.conversation_id)}
                          onChange={() => toggleSelection(recording)}
                          className="rounded text-teal-600 focus:ring-teal-500"
                        />
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm">
                          <div className="font-medium text-gray-900 dark:text-white">
                            {recording.user_email || 'Unknown User'}
                          </div>
                          {recording.parrot_name && (
                            <div className="text-gray-500 dark:text-gray-400">
                              {recording.parrot_name}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex gap-1">
                          {recording.has_audio && (
                            <button
                              onClick={() => handleFilePreview(recording.conversation_id || recording.id, 'audio')}
                              className="px-2 py-1 text-xs bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-100 rounded hover:bg-green-200 dark:hover:bg-green-700"
                              title="Preview audio"
                            >
                              🎵
                            </button>
                          )}
                          {recording.has_transcript && (
                            <button
                              onClick={() => handleFilePreview(recording.conversation_id || recording.id, 'transcript')}
                              className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-100 rounded hover:bg-blue-200 dark:hover:bg-blue-700"
                              title="Preview transcript"
                            >
                              📄
                            </button>
                          )}
                          {recording.has_raw_log && (
                            <button
                              onClick={() => handleFilePreview(recording.conversation_id || recording.id, 'raw_log')}
                              className="px-2 py-1 text-xs bg-purple-100 dark:bg-purple-800 text-purple-800 dark:text-purple-100 rounded hover:bg-purple-200 dark:hover:bg-purple-700"
                              title="Preview raw logs"
                            >
                              📊
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">
                        <div>{formatDuration(recording.total_duration_ms)}</div>
                        {recording.total_turns && (
                          <div className="text-gray-500 dark:text-gray-400">
                            {recording.total_turns} turns
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">
                        {recording.created_at
                          ? formatDistanceToNow(new Date(recording.created_at), { addSuffix: true })
                          : 'Unknown'
                        }
                      </td>
                      <td className="px-4 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          recording.is_deleted
                            ? 'bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-100'
                            : 'bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-100'
                        }`}>
                          {recording.is_deleted ? 'Deleted' : 'Active'}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm">
                        <div className="flex gap-1">
                          {recording.is_deleted ? (
                            <button
                              onClick={() => handleRestore(recording)}
                              className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-100 rounded hover:bg-blue-200 dark:hover:bg-blue-700"
                              title="Restore recording"
                            >
                              Restore
                            </button>
                          ) : (
                            <button
                              onClick={() => handlePermanentDelete(recording)}
                              className="px-2 py-1 text-xs bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-100 rounded hover:bg-red-200 dark:hover:bg-red-700"
                              title="Permanently delete"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="text-sm text-gray-700 dark:text-gray-300">
              Showing {((currentPage - 1) * pagination.limit) + 1} to {Math.min(currentPage * pagination.limit, pagination.total)} of {pagination.total} results
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => fetchRecordings(currentPage - 1)}
                disabled={!pagination.hasPrev}
                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:text-white"
              >
                Previous
              </button>
              <span className="px-3 py-1 text-sm dark:text-gray-300">
                Page {currentPage} of {pagination.totalPages}
              </span>
              <button
                onClick={() => fetchRecordings(currentPage + 1)}
                disabled={!pagination.hasNext}
                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:text-white"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* File Preview Modal */}
        {showFilePreview && filePreviewData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-screen overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  File Preview - {filePreviewData.fileName}
                </h3>
                <button
                  onClick={() => {
                    setShowFilePreview(false);
                    setFilePreviewData(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6">
                {/* File Metadata */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">Information</h4>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Size:</span>
                        <span className="ml-2 text-gray-900 dark:text-white">
                          {formatFileSize(filePreviewData.size)}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Modified:</span>
                        <span className="ml-2 text-gray-900 dark:text-white">
                          {filePreviewData.lastModified
                            ? new Date(filePreviewData.lastModified).toLocaleString()
                            : 'Unknown'
                          }
                        </span>
                      </div>
                      {filePreviewData.metadata && (
                        <>
                          <div>
                            <span className="text-gray-600 dark:text-gray-400">User:</span>
                            <span className="ml-2 text-gray-900 dark:text-white">
                              {filePreviewData.metadata.userEmail || 'Unknown'}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600 dark:text-gray-400">Parrot:</span>
                            <span className="ml-2 text-gray-900 dark:text-white">
                              {filePreviewData.metadata.parrotName || 'Unknown'}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600 dark:text-gray-400">Duration:</span>
                            <span className="ml-2 text-gray-900 dark:text-white">
                              {formatDuration(filePreviewData.metadata.duration)}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Content Preview for text files */}
                  {filePreviewData.fileType !== 'audio' && filePreviewData.contentPreview && (
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white mb-2">Content Preview</h4>
                      <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700 max-h-60 overflow-y-auto">
                        <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                          {filePreviewData.contentPreview}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={() => handleFileView(filePreviewData.conversationId, filePreviewData.fileType, false)}
                    className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
                  >
                    Open
                  </button>
                  <button
                    onClick={() => handleFileView(filePreviewData.conversationId, filePreviewData.fileType, true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Download
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminRoute>
  );
}